import React, { useState, useEffect, useCallback } from "react";
import { Plus, Download, Upload } from "lucide-react";
import { toast } from '../utils/toast';

import DoctorList from "../components/doctors/DoctorList";
import DoctorFormModal from '../components/doctors/DoctorFormModal';
import PageLayout from "../components/PageLayout";

import { Doctor, DoctorFormData, DoctorDatabasePageProps } from "../components/common/types";
import { addNewDoctor, deleteDoctor, editDoctor, getAlldoctors, PaginationMeta, DoctorServerFilters } from "../api/doctor";
import { useAuth } from "../context/AuthContext";

const exportToCsv = (filename: string, data: Doctor[]): void => {
  // const headers = [
  //   'Doctor Code', 'Doctor Name', 'City', 'Hospital Name', 'Employee',
  //   'Specialty', 'Qualification', 'Category', 'Zone', 'Date of Birth',
  //   'Address', 'Created Date'
  // ];

  // const csvContent = [
  //   headers.join(','),
  //   ...data.map((doctor: Doctor) => [
  //     doctor.code,
  //     doctor.name,
  //     doctor.city || '',
  //     doctor.hospital_name || '',
  //     doctor.employee_id ? mockEmployees.find(e => e.id === doctor.employee_id)?.name || '' : '',
  //     getSpecialtyName(doctor.specialization_id),
  //     doctor.qualification || '',
  //     getCategoryName(doctor.doctor_category_id),
  //     doctor.zone_id ? mockZones.find(z => z.id === doctor.zone_id)?.name || '' : '',
  //     doctor.dob ? new Date(doctor.dob).toLocaleDateString() : '',
  //     doctor.address || '',
  //     // doctor.created_date ? new Date(doctor.created_date).toLocaleDateString() : ''
  //   ].map(field => `"${field?.toString().replace(/"/g, '""') || ''}"`).join(','))
  // ].join('\n');

  // const blob = new Blob([csvContent], { type: 'text/csv' });
  // const url = window.URL.createObjectURL(blob);
  // const a = document.createElement('a');
  // a.href = url;
  // a.download = filename;
  // a.click();
  // window.URL.revokeObjectURL(url);
};

const DoctorDatabasePage: React.FC<DoctorDatabasePageProps> = ({ onLogout, onNavigate }) => {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [serverFilters, setServerFilters] = useState<DoctorServerFilters>({});

  const loadDoctors = useCallback(async (page: number = 1, filters: DoctorServerFilters = {}) => {
    if (!user?.tenant_id) return;

    setIsLoading(true);
    try {
      const result = await getAlldoctors(user.tenant_id, page, 15, filters);
      setDoctors(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Failed to load doctors:", error);
    }
    setIsLoading(false);
  }, [user?.tenant_id]);

  const handleServerFilterChange = (filters: DoctorServerFilters) => {
    setServerFilters(filters);
    loadDoctors(1, filters);
  };

  useEffect(() => {
    if (user?.tenant_id) {
      loadDoctors();
    }
  }, [user?.tenant_id, loadDoctors]);


  const handleEdit = (doctor: Doctor): void => {
    setEditingDoctor(doctor);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (doctorId: number | string): Promise<void> => {
    try {
      console.log('Attempting to delete doctor:', doctorId);
      await deleteDoctor(Number(doctorId));
      await loadDoctors();
    } catch (error) {
      console.error("Failed to delete doctor:", error);
    }
  };

  const handleSubmit = async (doctorData: DoctorFormData): Promise<void> => {
    try {
      console.log(doctorData);
      if (editingDoctor) {
        const res = await editDoctor(editingDoctor.id!, doctorData);
        if (res.status == 200) {
          toast.success("Doctor Updated Successfully!");
        }
      } else {
        const response = await addNewDoctor(doctorData);
        if (response.status == 200) {
          toast.success("Doctor Added Successfully!");
        }
      }
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      setEditingDoctor(null);
      await loadDoctors();
    } catch (error) {
      console.error("Failed to save doctor:", error);
      toast.error("Something went wrong!");
    }
  };

  const handleExport = (): void => {
    exportToCsv("doctors.csv", doctors);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log("File upload:", file.name);
    toast.info("CSV import functionality will be implemented with proper parsing");

    // Reset file input
    event.target.value = '';
  };

  return (
    <PageLayout onLogout={onLogout} currentPage="doctors" onNavigate={onNavigate}>
      <div className="p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Doctor</h1>
                <p className="text-blue-600 mt-1">Manage your doctor information</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => document.getElementById('import-doctors-input')?.click()}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </button>
                <input
                  type="file"
                  id="import-doctors-input"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".csv"
                />
                <button
                  onClick={handleExport}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </button>
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Doctor
                </button>
              </div>
            </div>

            {/* Doctor List */}
            <DoctorList
              doctors={doctors}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pagination={pagination}
              onPageChange={(page) => loadDoctors(page, serverFilters)}
              serverFilters={serverFilters}
              onServerFilterChange={handleServerFilterChange}
            />

            {/* Add Doctor Modal */}
            <DoctorFormModal
              isOpen={isAddModalOpen}
              onOpenChange={setIsAddModalOpen}
              onSubmit={handleSubmit}
              title="Add New Doctor"
              submitButtonText="Add Doctor"
              onCancel={() => console.log("cancel")}
            />

            {/* Edit Doctor Modal */}
            <DoctorFormModal
              isOpen={isEditModalOpen}
              onOpenChange={setIsEditModalOpen}
              doctor={editingDoctor}
              onSubmit={handleSubmit}
              title="Edit Doctor Information"
              submitButtonText="Update Doctor"
              onCancel={() => console.log("cancel")}
            />
          </div>
        </div>
    </PageLayout>
  );
};

export default DoctorDatabasePage;